import React from 'react';
import { useLanguage } from '../../context/LanguageContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${backendUrl}${url}`;
};

const SectionTitle = ({ section, isRTL }) => {
  const titleAr = section.title_ar || section.meta?.title_ar || '';
  const title = isRTL ? (titleAr || section.title || '') : (section.title || '');
  if (!title) return null;
  return (
    <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
      {title}
    </h2>
  );
};

const SectionBody = ({ section, isRTL }) => {
  const body = isRTL
    ? (section.content_ar || section.content || '')
    : (section.content || '');
  if (!body) return null;
  return (
    <div
      className={`page-content ${isRTL ? 'text-right' : 'text-left'}`}
      dangerouslySetInnerHTML={{ __html: body }}
    />
  );
};

const CustomCard = ({ section, isRTL, index }) => {
  const imageUrl = resolveUrl(section.image_url);
  const isImageLeft = index % 2 === 0;

  return (
    <section className="section-padding bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className={`grid grid-cols-1 ${imageUrl ? 'lg:grid-cols-2' : ''} gap-8 lg:gap-12 items-center`}>

          {imageUrl && (
            <div className={isImageLeft ? 'lg:order-1' : 'lg:order-2'}>
              <div className="relative max-w-md mx-auto lg:max-w-none">
                <div className="aspect-[4/3] overflow-hidden rounded-2xl">
                  <img
                    src={imageUrl}
                    alt={section.title || 'Section image'}
                    className="w-full h-full object-cover rounded-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="hidden sm:block absolute -bottom-3 -right-3 w-full h-full border border-gold/25 -z-10 rounded-2xl" />
              </div>
            </div>
          )}

          <div className={`${imageUrl ? (isImageLeft ? 'lg:order-2' : 'lg:order-1') : ''}`}>
            <SectionTitle section={section} isRTL={isRTL} />
            <div className="w-12 h-px bg-gold mb-6" />
            <SectionBody section={section} isRTL={isRTL} />
          </div>
        </div>
      </div>
    </section>
  );
};

const GallerySpotlight = ({ section, isRTL }) => {
  const imageUrl = resolveUrl(section.image_url);

  if (!imageUrl) return <CustomCard section={section} isRTL={isRTL} index={0} />;

  return (
    <section className="section-padding bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className={`flex flex-col ${isRTL ? 'items-end text-right' : 'items-start text-left'} mb-8`}>
          <SectionTitle section={section} isRTL={isRTL} />
          <SectionBody section={section} isRTL={isRTL} />
        </div>
        <div className="relative w-full aspect-[21/9] overflow-hidden rounded-2xl group">
          <img
            src={imageUrl}
            alt={section.title || 'Gallery'}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent pointer-events-none" />
        </div>
      </div>
    </section>
  );
};

const HeroBanner = ({ section, isRTL }) => {
  const titleAr = section.title_ar || section.meta?.title_ar || '';
  const title = isRTL ? (titleAr || section.title || '') : (section.title || '');
  const imageUrl = resolveUrl(section.image_url);

  return (
    <section
      className="relative section-padding overflow-hidden"
      style={imageUrl ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
    >
      {imageUrl && <div className="absolute inset-0 bg-charcoal/75" />}
      {!imageUrl && <div className="absolute inset-0 bg-charcoal" />}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-10 text-center">
        {title && (
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-cream leading-tight mb-4">
            {title}
          </h2>
        )}
        {title && <div className="w-16 h-px bg-gold mx-auto mb-6" />}
        <SectionBody section={section} isRTL={isRTL} />
      </div>
    </section>
  );
};

const CtaPanel = ({ section, isRTL }) => {
  const titleAr = section.title_ar || section.meta?.title_ar || '';
  const title = isRTL ? (titleAr || section.title || '') : (section.title || '');
  const settings = section.settings || {};
  const btnText = isRTL
    ? (settings.button_text_ar || settings.button_text || '')
    : (settings.button_text || '');
  const btnUrl = settings.button_url || '#';

  return (
    <section className="section-padding bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className="max-w-2xl mx-auto text-center premium-card p-8 sm:p-12 rounded-2xl border border-gold/20">
          {title && (
            <h2 className="font-serif text-3xl sm:text-4xl text-cream mb-3">{title}</h2>
          )}
          <SectionBody section={section} isRTL={isRTL} />
          {btnText && (
            <a href={btnUrl} className="btn-gold inline-block mt-6">
              {btnText}
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

const DynamicSection = ({ section, index = 0 }) => {
  const { isRTL } = useLanguage();

  switch (section.section_type) {
    case 'gallery':
      return <GallerySpotlight section={section} isRTL={isRTL} />;
    case 'hero':
    case 'banner':
      return <HeroBanner section={section} isRTL={isRTL} />;
    case 'cta':
      return <CtaPanel section={section} isRTL={isRTL} />;
    default:
      return <CustomCard section={section} isRTL={isRTL} index={index} />;
  }
};

export default DynamicSection;
